// Admin-side classifier for Plaid IDV failure_details snapshots.
// Kept in sync with src/utils/idvFailureReason.ts in resident-portal-app.
// If the precedence ladder changes there, update here.

const isNotMatch = v => v !== undefined && v !== 'match';

function pickLatestAttempt(items) {
  if (!items?.length) return undefined;
  let best;
  let bestAttempt = -Infinity;
  items.forEach((it, i) => {
    const a = it.attempt ?? i;
    if (a >= bestAttempt) {
      bestAttempt = a;
      best = it;
    }
  });
  return best;
}

function collectDocumentary(vr) {
  const docs = vr.documents || [];
  const ordered = [...docs].sort((a, b) => (b.attempt ?? -Infinity) - (a.attempt ?? -Infinity));
  for (const doc of ordered) {
    const { fraudAnalysisDetails: f, imageQualityDetails: q, inputComparisons: input } = doc;
    if (!f && !q && !input) continue;
    const out = [];
    if (isNotMatch(input?.dateOfBirth)) out.push('KYC_DOB_MISMATCH');
    if (isNotMatch(input?.name)) out.push('KYC_NAME_MISMATCH');
    if (f?.imageCompositionCheck === 'failed') out.push('DOC_COMPOSITION');
    if (q?.blurCheck === 'failed' || q?.glareCheck === 'failed' || q?.dimensionsCheck === 'failed') {
      out.push('DOC_IMAGE_QUALITY');
    }
    if (
      f?.detailCheck === 'failed' ||
      f?.integrityCheck === 'failed' ||
      f?.portraitDetailsCheck === 'failed' ||
      f?.portraitPresenceCheck === 'failed' ||
      f?.issueDateCheck === 'failed'
    ) {
      out.push('DOC_AUTHENTICITY');
    }
    if (f?.typeSupported === 'failed') out.push('DOC_UNSUPPORTED');
    return out;
  }
  return [];
}

function collectSelfie(sc) {
  const last = pickLatestAttempt(sc.selfies || []);
  if (!last) return ['SELFIE_GENERIC'];
  const out = [];
  if (last.livenessCheck === 'failed') out.push('SELFIE_LIVENESS');
  if (isNotMatch(last.documentComparison)) out.push('SELFIE_NO_MATCH');
  return out.length ? out : ['SELFIE_GENERIC'];
}

function collectKyc(kyc) {
  const out = [];
  if (isNotMatch(kyc.dateOfBirth)) out.push('KYC_DOB_MISMATCH');
  if (isNotMatch(kyc.name)) out.push('KYC_NAME_MISMATCH');
  if (isNotMatch(kyc.address)) out.push('KYC_ADDRESS_MISMATCH');
  if (isNotMatch(kyc.idNumber)) out.push('KYC_ID_MISMATCH');
  if (isNotMatch(kyc.phoneNumber)) out.push('KYC_PHONE_MISMATCH');
  return out.length ? out : ['KYC_GENERIC'];
}

export function getIdvFailureReasons(envelope) {
  if (!envelope) return [];
  const status = (envelope.status || '').toUpperCase();
  if (status === 'SUCCESS') return [];
  if (status === 'ACTIVE' || status === 'PENDING') {
    return [{ code: 'INCOMPLETE', primaryStep: 'verify_sms', retriable: true }];
  }
  if (status !== 'FAILED') return [];
  const details = envelope.failureDetails;
  if (!details) return [{ code: 'UNKNOWN', primaryStep: 'unknown', retriable: true }];

  const steps = details.steps || {};
  const out = [];
  if (steps.documentaryVerification === 'failed' && details.documentaryVerification) {
    for (const code of collectDocumentary(details.documentaryVerification)) {
      out.push({ code, primaryStep: 'documentary_verification', retriable: true });
    }
  }
  if (steps.selfieCheck === 'failed' && details.selfieCheck) {
    for (const code of collectSelfie(details.selfieCheck)) {
      out.push({ code, primaryStep: 'selfie_check', retriable: true });
    }
  }
  if (steps.kycCheck === 'failed' && details.kycCheck) {
    for (const code of collectKyc(details.kycCheck)) {
      out.push({ code, primaryStep: 'kyc_check', retriable: true });
    }
  }
  if (steps.riskCheck === 'failed') {
    out.push({ code: 'RISK_DECLINED', primaryStep: 'risk_check', retriable: false });
  }
  if (steps.watchlistScreening === 'failed') {
    out.push({ code: 'WATCHLIST_FLAGGED', primaryStep: 'watchlist_screening', retriable: false });
  }
  const seen = new Set();
  const deduped = out.filter(r => (seen.has(r.code) ? false : (seen.add(r.code), true)));
  return deduped.length ? deduped : [{ code: 'UNKNOWN', primaryStep: 'unknown', retriable: true }];
}

// Plain-English copy for admins. Different from the resident-facing app copy:
// admins want diagnostic detail, residents want "fix this and retry".
export const adminFailureLabel = {
  DOC_IMAGE_QUALITY: 'Document image quality failed (blur / glare / dimensions)',
  DOC_COMPOSITION: 'Document image composition failed',
  DOC_AUTHENTICITY: 'Document authenticity check failed',
  DOC_UNSUPPORTED: 'Document type not supported',
  DOC_GENERIC: 'Document verification failed',
  SELFIE_LIVENESS: 'Selfie failed liveness check',
  SELFIE_NO_MATCH: 'Selfie did not match document portrait',
  SELFIE_GENERIC: 'Selfie verification failed',
  KYC_DOB_MISMATCH: 'Date of birth mismatch',
  KYC_NAME_MISMATCH: 'Name mismatch',
  KYC_ADDRESS_MISMATCH: 'Address mismatch',
  KYC_ID_MISMATCH: 'ID number mismatch',
  KYC_PHONE_MISMATCH: 'Phone number mismatch',
  KYC_GENERIC: 'KYC mismatch',
  RISK_DECLINED: 'Risk check declined (non-retriable)',
  WATCHLIST_FLAGGED: 'Watchlist screening flagged (non-retriable)',
  INCOMPLETE: 'Verification not completed',
  UNKNOWN: 'Unknown failure',
};

/** Plain-English failure line for admin UI. Empty when status isn't FAILED / no reasons. */
export function formatIdvFailureDescription(session) {
  if (!session || String(session.status || '').toUpperCase() !== 'FAILED') return '';
  const reasons = getIdvFailureReasons({
    status: session.status,
    failureDetails: session.failureDetails ?? session.failure_details ?? null,
  });
  if (!reasons.length) return '';
  return reasons.map(r => adminFailureLabel[r.code] || r.code).join('; ');
}
